(() => {
  const norm = v => (v ?? "").toString().trim();
  const normLower = v => norm(v).toLowerCase();

  const parseCSV = text => {
    const rows = [];
    let row = [], cur = "", inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i], next = text[i + 1];
      if (inQuotes) {
        if (c === '"' && next === '"') { cur += '"'; i++; }
        else if (c === '"') inQuotes = false;
        else cur += c;
      } else {
        if (c === '"') inQuotes = true;
        else if (c === ',') { row.push(cur); cur = ""; }
        else if (c === '\n') { row.push(cur); rows.push(row); row = []; cur = ""; }
        else if (c !== '\r') cur += c;
      }
    }
    if (cur.length || row.length) { row.push(cur); rows.push(row); }
    return rows;
  };

  const toNumOrNull = v => {
    const s = norm(v);
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  const nearlyEqual = (a, b, eps = 1e-6) =>
    a === null || b === null ? false : Math.abs(a - b) < eps;

  /** Extract the numeric item key from CSV item values like "Skort (1579)" */
  const extractItemNumber = raw => {
    const s = norm(raw);
    // If the value is already just a number, return it
    if (/^\d+$/.test(s)) return s;
    // Extract number from parentheses: "Skort (1579)" → "1579"
    const m = s.match(/\((\d+[A-Za-z]*)\)/);
    return m ? m[1] : s;
  };

  /**
   * Normalize CSV size labels to match Shopify variant option values.
   * "3 Girls" → "3", "6X Girls" → "6X", "7 Half" → "7H", "10 Half" → "10H"
   */
  /**
   * Normalize CSV size labels to match Shopify variant option values.
   * "3 Girls" → "3", "6X Girls" → "6X", "7 Half" → "7H", "10 Half" → "10H"
   * Also handles pull-on/youth abbreviated sizes:
   *   "2T"/"3T" → "Toddler 2T"/"Toddler 3T"
   *   "YXS (5-6)" → "Youth XS (5-6)", "YM (10-12)" → "Youth Medium (10-12)", etc.
   */
  const normalizeSize = raw => {
    const s = norm(raw);

    // Toddler sizes: "2T" → "Toddler 2T"
    if (/^\d+T$/i.test(s)) return "Toddler " + s;

    // Youth abbreviated sizes: "YXXS (3-4)" → "Youth XXS (3-4)"
    const youthMatch = s.match(/^Y(XXXS|XXS|XS|S|M|L|XL)\s*(\(.+\))$/i);
    if (youthMatch) {
      const abbr = youthMatch[1].toUpperCase();
      const range = youthMatch[2];
      const nameMap = { XXXS: "XXXS", XXS: "XXS", XS: "XS", S: "S", M: "Medium", L: "Large", XL: "XL" };
      return "Youth " + (nameMap[abbr] || abbr) + " " + range;
    }

    const parts = s.split(/\s+/);
    if (parts.length < 2) return s;
    const num = parts[0];
    const cat = parts.slice(1).join(" ").toLowerCase();
    if (cat === "girls" || cat === "junior") return num;
    if (cat === "half") return num + "H";
    return s; // unknown category, return as-is
  };

  const sizeSortKey = label => {
    const s = norm(label);
    const parts = s.split(/\s+/);
    const code = parts[0] || s;
    const rest = parts.slice(1).join(" ");
    const group = normLower(rest);
    const groupRank = group.includes("girls") ? 0 : group.includes("half") ? 1 : group.includes("junior") ? 2 : 9;
    let num = Number.POSITIVE_INFINITY;
    const xMatch = code.match(/^(\d+)(x)$/i);
    if (xMatch) num = Number(xMatch[1]) + 0.5;
    else if (/^0+$/.test(code)) num = 0;
    else if (/^\d+$/.test(code)) num = Number(code);
    return { groupRank, num, fullLower: normLower(s) };
  };

  const styleRank = s => {
    const t = normLower(s);
    return !t || t === "regular" ? 0 : t === "slim" ? 1 : 9;
  };

  const comboComparator = (a, b) => {
    const ka = sizeSortKey(a.size), kb = sizeSortKey(b.size);
    if (ka.groupRank !== kb.groupRank) return ka.groupRank - kb.groupRank;
    if (ka.num !== kb.num) return ka.num - kb.num;
    const sa = styleRank(a.style), sb = styleRank(b.style);
    return sa !== sb ? sa - sb : (a.display || "").localeCompare(b.display || "");
  };

  const roots = Array.from(document.querySelectorAll("[data-ma-sizing]"))
    .filter(el => el.getClientRects().length);

  if (!roots.length) return;

  // Alias map: text-based sizing_help metafield values → numeric CSV item keys
  const ITEM_ALIASES = {
    "pull-on shorts": "1334",
    "pull-on pants": "1387",
  };

  // Known color keywords for matching cross-product recommendations to the same color
  const COLOR_KEYWORDS = ["khaki", "navy", "black", "grey", "gray", "white", "blue", "red", "green", "plaid", "charcoal", "hunter"];

  const extractColor = title => {
    const t = normLower(title);
    return COLOR_KEYWORDS.find(c => t.includes(c)) || "";
  };

  roots.forEach(root => {
    const rawItemKey = (root.dataset.item || root.dataset.style || "").trim();
    const itemKey = ITEM_ALIASES[rawItemKey.toLowerCase()] || rawItemKey;
    const csvUrl = root.dataset.sizingCsvUrl;
    const productTitle = root.dataset.productTitle || "";

    const elToggle = root.querySelector("[data-ma-toggle]");
    const elPanel = root.querySelector("[data-ma-panel]");
    const elSize = root.querySelector("[data-ma-size]");
    const elWaist = root.querySelector("[data-ma-waist]");
    const elMeasurements = root.querySelector("[data-ma-measurements]");
    const elDl = root.querySelector("[data-ma-dl]");
    const elMatch = root.querySelector("[data-ma-match]");
    const elMatches = root.querySelector("[data-ma-waist-matches]");
    const elStatus = root.querySelector("[data-ma-status]");
    const variantsJsonEl = root.querySelector("[data-ma-variants]");

    const setStatus = (msg, kind) => {
      if (!elStatus) return;
      elStatus.textContent = msg || "";
      elStatus.classList.remove("is-error", "is-ok");
      if (kind) elStatus.classList.add(kind === "error" ? "is-error" : "is-ok");
    };

    const setMatchText = msg => { if (elMatch) elMatch.textContent = msg || ""; };

    const clearCards = () => {
      if (!elMatches) return;
      elMatches.innerHTML = "";
      elMatches.hidden = true;
      elMatches.removeAttribute("style");
    };

    const renderMeasurements = row => {
      if (!elDl || !elMeasurements) return;
      elDl.innerHTML = "";
      if (!row) { elMeasurements.hidden = true; return; }

      [["Waist", row.waist, row.waistMax],
       ["Hips", row.hips, row.hipsMax],
       ["Length", row.length, row.lengthMax]]
        .filter(([, v]) => v != null)
        .forEach(([label, val, valMax]) => {
          const dt = document.createElement("dt");
          dt.textContent = label;
          const dd = document.createElement("dd");
          // Show range if max value differs from min
          if (valMax != null && valMax !== val) {
            dd.textContent = `${val}–${valMax}`;
          } else {
            dd.textContent = String(val);
          }
          elDl.appendChild(dt);
          elDl.appendChild(dd);
        });
      elMeasurements.hidden = false;
    };

    // Toggle panel
    if (elToggle && elPanel) {
      elToggle.addEventListener("click", () => {
        const isOpen = elToggle.getAttribute("aria-expanded") === "true";
        elToggle.setAttribute("aria-expanded", String(!isOpen));
        elPanel.hidden = isOpen;
        if (!isOpen) setTimeout(() => elSize?.focus?.(), 0);
      });
    }

    if (!csvUrl || !itemKey) {
      setStatus(`Missing config. item="${itemKey}" csvUrl="${csvUrl}"`, "error");
      return;
    }

    if (!elSize || !elWaist || !elMeasurements || !elDl) {
      setStatus("Sizing widget markup incomplete (missing selects/measurements).", "error");
      return;
    }

    // --- Form / variant helpers ---
    const formScope = root.closest(".product__info-container")
      || document.querySelector(".product__info-container") || document;

    const getIdSelect = () =>
      formScope.querySelector('form[action^="/cart/add"] select[name="id"]')
      || document.querySelector('form[action^="/cart/add"] select[name="id"]');

    const getOptionSelectByDataIndex = dataIndex =>
      formScope.querySelector(`select[data-index="${dataIndex}"]`)
      || document.querySelector(`select[data-index="${dataIndex}"]`);

    const getOptionFieldsetByLabel = labelLower => {
      const formEl = formScope.querySelector('form[action^="/cart/add"]')
        || document.querySelector('form[action^="/cart/add"]');
      return formEl && Array.from(formEl.querySelectorAll("fieldset"))
        .find(fs => (fs.textContent || "").toLowerCase().includes(labelLower)) || null;
    };

    const sizeFs = getOptionFieldsetByLabel("size");
    const styleFs = getOptionFieldsetByLabel("style");

    const getSelectedFromFieldset = fs => {
      if (!fs) return "";
      const checked = fs.querySelector('input[type="radio"]:checked');
      if (checked) return checked.value;
      const pressed = fs.querySelector('[aria-pressed="true"]');
      return (pressed && pressed.textContent) || "";
    };

    const setSelectedInFieldset = (fs, value) => {
      if (!fs) return false;
      const target = normLower(value);
      const match = Array.from(fs.querySelectorAll('input[type="radio"]'))
        .find(r => normLower(r.value) === target);
      if (!match) return false;
      match.checked = true;
      match.dispatchEvent(new Event("change", { bubbles: true }));
      match.dispatchEvent(new Event("click", { bubbles: true }));
      return true;
    };

    const getImpulseSize = () => norm(getSelectedFromFieldset(sizeFs));
    const getImpulseStyle = () => norm(getSelectedFromFieldset(styleFs));

    const parseVariants = () => {
      if (!variantsJsonEl) return null;
      try { return JSON.parse(variantsJsonEl.textContent || "{}"); } catch { return null; }
    };

    const buildVariantLookup = () => {
      const parsed = parseVariants();
      if (!parsed) return null;
      const options = parsed.options || [];
      const variants = parsed.variants || [];
      const sizeIdx = options.findIndex(o => normLower(o) === "size");
      const styleIdx = options.findIndex(o => normLower(o) === "style");
      const keyToId = new Map();
      const existingKeys = new Set();
      variants.forEach(v => {
        const size = sizeIdx >= 0 ? norm(v[`option${sizeIdx + 1}`]) : "";
        const style = styleIdx >= 0 ? norm(v[`option${styleIdx + 1}`]) : "";
        if (!size) return;
        const key = `${normLower(size)}||${normLower(style)}`;
        keyToId.set(key, v.id);
        existingKeys.add(key);
      });
      return { options, sizeIdx, styleIdx, keyToId, existingKeys };
    };

    const setImpulseOptionSelect = (optIndex1Based, value) => {
      const sel = getOptionSelectByDataIndex(`option${optIndex1Based}`);
      if (!sel) return false;
      const target = norm(value);
      const opt = Array.from(sel.options).find(o => norm(o.value) === target || norm(o.textContent) === target);
      if (!opt) return false;
      sel.value = opt.value;
      // Temporarily suppress errors from theme's variant handler (getThumbIndex
      // crashes on single-image products when it can't find a thumbnail element).
      const suppress = e => { e.preventDefault(); };
      window.addEventListener("error", suppress);
      try {
        sel.dispatchEvent(new Event("change", { bubbles: true }));
        sel.dispatchEvent(new Event("input", { bubbles: true }));
      } catch (_) {}
      window.removeEventListener("error", suppress);
      return true;
    };

    const applyVariantAndUi = row => {
      const lookup = buildVariantLookup();
      const idSel = getIdSelect();
      if (!lookup || !idSel) return { ok: false };
      const size = norm(row.size);
      const style = norm(row.style);
      const wantStyle = lookup.styleIdx >= 0 ? style : "";
      const key = `${normLower(size)}||${normLower(wantStyle)}`;
      const variantId = lookup.keyToId.get(key);
      if (!variantId) return { ok: false };
      idSel.value = String(variantId);
      idSel.dispatchEvent(new Event("change", { bubbles: true }));
      idSel.dispatchEvent(new Event("input", { bubbles: true }));
      if (lookup.sizeIdx >= 0) setImpulseOptionSelect(lookup.sizeIdx + 1, size);
      if (lookup.styleIdx >= 0) setImpulseOptionSelect(lookup.styleIdx + 1, wantStyle);
      setSelectedInFieldset(sizeFs, size);
      if (wantStyle) setSelectedInFieldset(styleFs, wantStyle);
      return { ok: true, variantId };
    };

    const buildCardDl = row => {
      const dl = document.createElement("dl");
      dl.className = "ma-sizing__match-dl";
      [["Waist", row.waist, row.waistMax],
       ["Hips", row.hips, row.hipsMax],
       ["Length", row.length, row.lengthMax]]
        .filter(([, v]) => v != null)
        .forEach(([label, val, valMax]) => {
          const dt = document.createElement("dt");
          dt.textContent = label;
          const dd = document.createElement("dd");
          if (valMax != null && valMax !== val) {
            dd.textContent = `${val}\u2013${valMax}`;
          } else {
            dd.textContent = String(val);
          }
          dl.appendChild(dt);
          dl.appendChild(dd);
        });
      return dl;
    };

    // Pants ↔ shorts pairing for cross-product recommendations
    const pantsShortsMap = {
      "1334": "1387", "1387": "1334",
      "pull-on shorts": "pull-on pants", "pull-on pants": "pull-on shorts",
      "shorts": "pants", "pants": "shorts",
    };

    const showMatchCards = (matches, otherMatches) => {
      if (!elMatches) return;
      elMatches.innerHTML = "";
      elMatches.hidden = false;
      // Ensure parent measurements container is visible (cards live inside it)
      if (elMeasurements) elMeasurements.hidden = false;

      // Cards for sizes on THIS product
      matches.forEach(row => {
        const card = document.createElement("div");
        card.className = "ma-sizing__match-card";

        const header = document.createElement("div");
        header.className = "ma-sizing__match-card-header";
        const sizeLabel = document.createElement("div");
        sizeLabel.className = "ma-sizing__match-size";
        sizeLabel.textContent = row.display;
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "ma-sizing__match-select";
        btn.textContent = "Select";

        const applyThis = () => {
          elSize.value = row.comboKey;
          elSize.dispatchEvent(new Event("change", { bubbles: true }));
          clearCards();
        };
        btn.addEventListener("click", e => { e.preventDefault(); e.stopPropagation(); applyThis(); });
        card.addEventListener("click", applyThis);
        header.appendChild(sizeLabel);
        header.appendChild(btn);

        card.appendChild(header);
        card.appendChild(buildCardDl(row));
        elMatches.appendChild(card);
      });

      // Cards for sizes on OTHER products (different fit/range)
      if (otherMatches && otherMatches.length) {
        const divider = document.createElement("div");
        divider.className = "ma-sizing__other-heading";
        const pairedRaw = pantsShortsMap[normLower(itemKey)];
        const pairedLabel = pairedRaw
          ? (/^\d+$/.test(pairedRaw) ? "performance fabric" : pairedRaw.replace(/^\w/, c => c.toUpperCase()))
          : "a different fit";
        divider.textContent = `Also available in ${pairedLabel}:`;
        elMatches.appendChild(divider);

        otherMatches.forEach(row => {
          const card = document.createElement("div");
          card.className = "ma-sizing__match-card ma-sizing__match-card--other";

          const header = document.createElement("div");
          header.className = "ma-sizing__match-card-header";
          const sizeLabel = document.createElement("div");
          sizeLabel.className = "ma-sizing__match-size";
          sizeLabel.textContent = row.display;

          const viewBtn = document.createElement("button");
          viewBtn.type = "button";
          viewBtn.className = "ma-sizing__match-view";
          viewBtn.textContent = "View product";
          viewBtn.addEventListener("click", e => {
            e.preventDefault();
            e.stopPropagation();
            // Search for the product by item key + same color on the store
            const color = extractColor(productTitle);
            const searchTerm = color ? `${row.item} ${color}` : row.item;
            const q = encodeURIComponent(searchTerm);
            window.open(`/search?q=${q}`, "_blank");
          });

          header.appendChild(sizeLabel);
          header.appendChild(viewBtn);
          card.appendChild(header);
          card.appendChild(buildCardDl(row));
          elMatches.appendChild(card);
        });
      }
    };

    // --- Fetch and parse CSV ---
    fetch(csvUrl, { credentials: "same-origin", cache: "force-cache" })
      .then(r => r.ok ? r.text() : Promise.reject(new Error("Could not load sizing-data.csv")))
      .then(csvText => {
        const normalized = csvText.includes("\t") ? csvText.replace(/\t/g, ",") : csvText;
        const grid = parseCSV(normalized);
        const header = (grid.shift() || []).map(normLower);

        const idx = name => header.indexOf(name);

        // Support both old format (waist, hips, length) and new format (waist_min/max, hip_min/max, inseam_min/max)
        const iItem = idx("item");
        const iSize = idx("size");
        const iStyle = idx("style");

        // Waist: try "waist" first, fall back to "waist_min"
        let iWaist = idx("waist");
        let iWaistMax = -1;
        if (iWaist < 0) {
          iWaist = idx("waist_min");
          iWaistMax = idx("waist_max");
        }

        // Hips: try "hips" first, fall back to "hip_min"
        let iHips = idx("hips");
        let iHipsMax = -1;
        if (iHips < 0) {
          iHips = idx("hip_min");
          iHipsMax = idx("hip_max");
        }

        // Length: try "length" first, fall back to "inseam_min"
        let iLength = idx("length");
        let iLengthMax = -1;
        if (iLength < 0) {
          iLength = idx("inseam_min");
          iLengthMax = idx("inseam_max");
        }

        if (iItem < 0 || iSize < 0 || iWaist < 0) {
          setStatus("CSV header must include: ITEM, SIZE, and WAIST (or WAIST_MIN)", "error");
          return;
        }

        const existingKeys = buildVariantLookup()?.existingKeys || new Set();

        // Helper: treat 0-100 placeholder ranges as null (no real data)
        const cleanRange = (min, max) => (min === 0 && max === 100) ? [null, null] : [min, max];

        // Parse a CSV row into a sizing object
        const parseRow = r => {
          const item = norm(r[iItem]);
          const rawSize = norm(r[iSize]);
          const normSize = normalizeSize(rawSize);
          const style = iStyle >= 0 ? norm(r[iStyle]) : "";
          const waist = toNumOrNull(r[iWaist]);
          let splitSize = rawSize, splitStyle = style;
          if (iStyle < 0 && !style) {
            const lastSpace = rawSize.lastIndexOf(" ");
            if (lastSpace > 0) {
              const maybeSz = rawSize.slice(0, lastSpace);
              const maybeSt = rawSize.slice(lastSpace + 1);
              if (/^(regular|slim|husky)$/i.test(maybeSt)) { splitSize = maybeSz; splitStyle = maybeSt; }
            }
          }
          const rawComboNorm = `${normLower(rawSize)}||${normLower(style)}`;
          const normComboNorm = `${normLower(normSize)}||${normLower(style)}`;
          const splitComboNorm = `${normLower(splitSize)}||${normLower(splitStyle)}`;
          const waistMax = iWaistMax >= 0 ? toNumOrNull(r[iWaistMax]) : null;
          const [cWaist, cWaistMax] = cleanRange(waist, waistMax);
          const rawHips = iHips >= 0 ? toNumOrNull(r[iHips]) : null;
          const rawHipsMax = iHipsMax >= 0 ? toNumOrNull(r[iHipsMax]) : null;
          const [cHips, cHipsMax] = cleanRange(rawHips, rawHipsMax);
          const rawLength = iLength >= 0 ? toNumOrNull(r[iLength]) : null;
          const rawLengthMax = iLengthMax >= 0 ? toNumOrNull(r[iLengthMax]) : null;
          const [cLength, cLengthMax] = cleanRange(rawLength, rawLengthMax);
          return { item, rawSize, normSize, style, waist: cWaist, splitSize, splitStyle,
            waistMax: cWaistMax, hips: cHips, hipsMax: cHipsMax, length: cLength, lengthMax: cLengthMax,
            rawComboNorm, normComboNorm, splitComboNorm };
        };

        const nonEmptyRows = grid.filter(r => r.some(cell => norm(cell) !== ""));

        const matchesItem = (row, key) => {
          const csvItem = normLower(row.item);
          if (csvItem === key) return true;
          return normLower(extractItemNumber(row.item)) === key;
        };

        const itemRowsRaw = nonEmptyRows.map(parseRow)
          .filter(r => matchesItem(r, normLower(itemKey)));

        if (!itemRowsRaw.length) {
          setStatus(`No sizing rows found for ITEM "${itemKey}"`, "error");
          return;
        }

        // Determine which size format matches the variants.
        // Try: raw ("3 Girls"), normalized ("3"/"7H"), split ("3" + style "Regular")
        let matchMode = "raw"; // "raw" | "norm" | "split"
        let itemRows;
        if (existingKeys.size) {
          const rawMatches = itemRowsRaw.filter(r => existingKeys.has(r.rawComboNorm));
          const normMatches = itemRowsRaw.filter(r => existingKeys.has(r.normComboNorm));
          const splitMatches = itemRowsRaw.filter(r => existingKeys.has(r.splitComboNorm));
          const best = [
            { mode: "raw", rows: rawMatches },
            { mode: "norm", rows: normMatches },
            { mode: "split", rows: splitMatches },
          ].sort((a, b) => b.rows.length - a.rows.length)[0];
          if (best.rows.length > 0) {
            itemRows = best.rows;
            matchMode = best.mode;
          } else {
            itemRows = itemRowsRaw; // no variant filter match, show all
          }
        } else {
          itemRows = itemRowsRaw;
        }

        // Assign final size/comboKey/display based on chosen format
        const assignDisplayFields = rows => {
          rows.forEach(r => {
            if (matchMode === "split") {
              r.size = r.splitSize;
              r.style = r.splitStyle;
            } else if (matchMode === "norm") {
              r.size = r.normSize;
            } else {
              r.size = r.rawSize;
            }
            r.comboKey = `${r.size}||${r.style}`;
            r.comboNorm = matchMode === "split" ? r.splitComboNorm
              : matchMode === "norm" ? r.normComboNorm : r.rawComboNorm;
            r.display = r.style ? `${r.size} ${r.style}` : r.size;
          });
        };
        assignDisplayFields(itemRows);

        // Cross-product recommendations: only for pants ↔ shorts pairs.
        // Find the paired item in the CSV (e.g., "Pull-on Pants" when viewing "Pull-on Shorts").
        const pairedItem = pantsShortsMap[normLower(itemKey)];
        const otherRows = pairedItem
          ? nonEmptyRows.map(parseRow).filter(r => matchesItem(r, pairedItem))
          : [];
        assignDisplayFields(otherRows);

        // Populate size dropdown
        elSize.innerHTML = '<option value="">Select size\u2026</option>';
        Array.from(new Map(itemRows.map(r => [r.comboKey, r])).values())
          .sort(comboComparator)
          .forEach(row => {
            const opt = document.createElement("option");
            opt.value = row.comboKey;
            opt.textContent = row.display;
            elSize.appendChild(opt);
          });

        // Populate waist dropdown (include waist values from other sizing ranges too)
        elWaist.innerHTML = '<option value="">Select waist\u2026</option>';
        const allWaists = [...itemRows, ...otherRows].map(r => r.waist).filter(w => w !== null);
        Array.from(new Set(allWaists))
          .map(w => Number(w))
          .filter(n => Number.isFinite(n))
          .sort((a, b) => a - b)
          .forEach(w => {
            const opt = document.createElement("option");
            opt.value = String(w);
            opt.textContent = String(w);
            elWaist.appendChild(opt);
          });

        const findByComboKey = comboKey => itemRows.find(r => r.comboKey === comboKey);

        const waistFilter = (rows, w) => rows.filter(r => {
          if (r.waist === null) return false;
          if (r.waistMax !== null) return w >= r.waist && w <= r.waistMax;
          return nearlyEqual(r.waist, w);
        });

        const findMatchesByWaist = waistVal => {
          const w = Number(norm(waistVal));
          if (!Number.isFinite(w)) return { matches: [], otherMatches: [] };
          return {
            matches: waistFilter(itemRows, w),
            otherMatches: waistFilter(otherRows, w)
          };
        };

        const clearOther = which => {
          if (which === "size") elWaist.value = "";
          if (which === "waist") elSize.value = "";
        };

        elSize.addEventListener("change", () => {
          clearOther("size");
          clearCards();
          setMatchText("");
          setStatus("");
          const row = findByComboKey(elSize.value);
          if (!row) { renderMeasurements(null); return; }
          renderMeasurements(row);
          if (!applyVariantAndUi(row).ok) {
            setStatus("Could not apply variant for that size/style.", "error");
          }
        });

        elWaist.addEventListener("change", () => {
          clearOther("waist");
          clearCards();
          setMatchText("");
          setStatus("");
          const waistVal = elWaist.value;
          if (!waistVal) { renderMeasurements(null); return; }
          const { matches, otherMatches } = findMatchesByWaist(waistVal);
          if (!matches.length && !otherMatches.length) {
            setMatchText("No sizes found for that waist.");
            renderMeasurements(null);
            return;
          }
          if (matches.length === 1 && !otherMatches.length) {
            setMatchText("");
            elSize.value = matches[0].comboKey;
            elSize.dispatchEvent(new Event("change", { bubbles: true }));
            return;
          }
          if (matches.length) {
            setMatchText(matches.length === 1
              ? "Best match on this product:"
              : "Multiple sizes match \u2014 pick one below.");
          } else {
            setMatchText("No matches on this product, but other fits may work:");
          }
          renderMeasurements(null);
          showMatchCards(matches, otherMatches);
        });

        // Auto-select current variant's size
        const curSizeSel = getOptionSelectByDataIndex("option1");
        const curStyleSel = getOptionSelectByDataIndex("option2");
        const curSize = curSizeSel ? norm(curSizeSel.selectedOptions?.[0]?.textContent) : getImpulseSize();
        const curStyle = curStyleSel ? norm(curStyleSel.selectedOptions?.[0]?.textContent) : getImpulseStyle();
        const initialKey = curStyle ? `${curSize}||${curStyle}` : `${curSize}||`;
        if (Array.from(elSize.options).some(o => o.value === initialKey)) {
          elSize.value = initialKey;
          elSize.dispatchEvent(new Event("change", { bubbles: true }));
        }

        setStatus("");
      })
      .catch(err => {
        setStatus("Sizing data failed to load. Check Console/Network.", "error");
        console.error("Sizing widget error:", err);
      });
  });
})();
